import React, { useState } from 'react';
import {
    Modal,
    Input,
    message,
    Tooltip,
    Typography,
    Divider,
    Form,
    Row,
    Col,
    Select,
} from 'antd';
import { CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { post } from '@/common/services/httpClient';
import { useTranslation } from 'react-i18next';
import Button from 'antd/lib/button';

interface ClonePlanModalProps {
    planId: number | string;
    planName: string;
    apiUrl: string;
    queryKey?: string | unknown[];
    title?: string;
    successMessage?: string;
    confirmButtonText?: string;
}

interface ApiResponse<T = unknown> {
    data: T | null;
    paging: unknown;
    statusCode: number;
    isError: boolean;
    title?: string;
    developerMessage?: string;
    message?: string;
    messageArgs?: unknown;
}

interface DropdownItem {
    Id: number;
    Name: string;
}

const useDropdownData = (dropdownName: string) => {
    return useQuery({
        queryKey: ['dropdownData', dropdownName],
        queryFn: async (): Promise<{ label: string; value: number; labelAr?: string}[]> => {
            const response: Record<string, DropdownItem[]> = await post('/Lookup/GetDropdownData', { dropdowns: [dropdownName] });
            return response[dropdownName].map((item: DropdownItem) => ({ label: item.Name, value: item.Id }));
        },
        staleTime: Infinity, // Data is unlikely to change often
    });
};

const ClonePlanModal: React.FC<ClonePlanModalProps> = ({
    planId,
    planName,
    apiUrl,
    queryKey,
    title = 'Clone Plan',
    successMessage,
    confirmButtonText,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [form] = Form.useForm();

    const { data: companies, isLoading: isLoadingCompanies } = useDropdownData('companieswithoutplan');

    const mutation = useMutation<
        ApiResponse,
        Error,
        { planName: string; companyId: number; companyName: string }
    >({
        mutationFn: async ({ planName, companyId, companyName }) => {
            const response = (await post(apiUrl, {
                planId,
                planName,
                companyId,
                companyName,
            })) as ApiResponse;

            if (response?.isError) {
                Modal.destroyAll();
                setIsModalVisible(false);
                form.resetFields();
                throw new Error(response?.title || 'An error occurred');
            }

            return response;
        },
        onSuccess: () => {
            message.success(successMessage || t('Plan cloned successfully'));
            if (queryKey) {
                queryClient.invalidateQueries({
                    queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
                });

                queryClient.invalidateQueries({
                    queryKey: ['dropdownData', 'companieswithoutplan'],
                });
            }
        },
        onError: (err) => {
            message.error(err.message || t('An error occurred'));
        },
    });

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            Modal.confirm({
                title: confirmButtonText || t('Are you sure you want to clone this plan?'),
                icon: <ExclamationCircleOutlined />,
                okText: t('Yes'),
                okType: 'primary',
                cancelText: t('No'),
                onOk: async () => {
                    setConfirmLoading(true);
                    try {
                        const selectedCompany = companies?.find(
                            c => c.value === values.companyId
                        );

                        await mutation.mutateAsync({
                            planName: values.planName,
                            companyId: values.companyId,
                            companyName: selectedCompany?.label || '',
                        });
                        setIsModalVisible(false);
                        form.resetFields();
                    } finally {
                        setConfirmLoading(false);
                    }
                },
            });
        } catch {
            // form validation failed
        }
    };

    const openModal = (e: React.MouseEvent) => {
        e.stopPropagation();
        form.setFieldsValue({
            planName: `${planName} - Clone`,
        });
        setIsModalVisible(true);
    };

    return (
        <>
            <Tooltip title={t(title)}>
                <Button
                    style={{ cursor: 'pointer', color: 'green' }}
                    icon={<CopyOutlined />}
                    onClick={openModal}
                />
            </Tooltip>

            <Modal
                title={t(title)}
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={confirmLoading}
                okText={t('Save')}
                cancelText={t('Cancel')}
                maskClosable={false}
            >
                <Divider />
                <Typography.Text>
                    <strong>{t('Original Plan')}:</strong> {planName}
                </Typography.Text>
                <Divider />

                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="companyId" label={t('Company Name')} rules={[{ required: true, message: t('Please select a company') }]} >
                                <Select placeholder={t('Select a company')} loading={isLoadingCompanies} size="large"
                                    showSearch={true}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {companies?.map(company => (
                                        <Option key={company.value} value={company.value} label={company.label}>{company.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label={t('Clone Plan Name')}
                        name="planName"
                        rules={[
                            { required: true, message: t('This field is required') },
                            {
                                min: 5,
                                max: 200,
                                message: t('Plan name must be between 5 and 200 characters.'),
                            },
                        ]}
                    >
                        <Input placeholder={t('Enter clone plan name')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ClonePlanModal;
